<script lang="ts">
	import BalanceCard from '$lib/components/cards/BalanceCard.svelte';
	import LoadingBalaceCard from '$lib/components/cards/LoadingBalaceCard.svelte';
	import NftBalanceCard from '$lib/components/cards/NFTBalanceCard.svelte';
	import EmptyState from '$lib/components/loading/EmptyState.svelte';
	import AddWrappedErc20 from '$lib/components/model/AddWrappedERC20.svelte';
	import FontSpan from '$lib/components/typography/FontSpan.svelte';
	import { AccountService } from '$lib/service/AccountService';
	import { GameService } from '$lib/service/GameService';
	import type { WalletBalances } from '$lib/service/WalletService';
	import { WalletService } from '$lib/service/WalletService';
	import { activeWallet, authenticatedUsingEmail, keycloak, userInfo } from '$lib/stores/userstore';
	import type { Account, GameWalletBalance, KeycloakAuth, Wallet } from '$lib/types';
	import { CONTRACT_ADDRESS } from '../../../../constants';

	let service: AccountService;
	let wservice: WalletService;
	let wallets: Wallet[] = [];
	$: if ($authenticatedUsingEmail && $keycloak) {
		service = new AccountService($keycloak);
		wservice = new WalletService($keycloak);
	}

	let isWalletsLoading = false;
	let walletBalances: WalletBalances | undefined;
	$: refreshWallets(service, $userInfo, $activeWallet);
	async function refreshWallets(
		service: AccountService,
		userInfo: Account | undefined,
		activeWallet: Wallet | undefined
	) {
		if (!!service && userInfo && activeWallet && !isWalletsLoading) {
			try {
				isWalletsLoading = true;
				walletBalances = await wservice.getWallets(activeWallet.address);
			} catch (error) {
				wallets = [];
			} finally {
				isWalletsLoading = false;
			}
		}
	}

	let balance: GameWalletBalance | undefined;
	async function loadGameWalletBalance(
		_keycloak: KeycloakAuth | undefined,
		_userInfo: Account | undefined
	) {
		if (_keycloak && _userInfo) {
			const service = new GameService(_keycloak);
			balance = await service.loadBalance(_userInfo.email);
		}
	}

	$: loadGameWalletBalance($keycloak, $userInfo);

	let showDepositDialog = false;
	let erc20ContractAddress = '';
	let currencyName = '';
	let network = '';
	let maxAmount = '';
	function depositeHandler(params: 'mainnet' | 'polygon') {
		showDepositDialog = true;
		network = params;
		currencyName = params === 'mainnet' ? 'WETH' : 'WMATIC';
		if (walletBalances) {
			maxAmount = params === 'mainnet' ? walletBalances.ethBalance : walletBalances.maticBalance;
		}
		erc20ContractAddress = CONTRACT_ADDRESS[params].ERC20;
	}
</script>

<svelte:head>
	<title>Wallet | My Dashboard</title>
</svelte:head>

<AddWrappedErc20
	{currencyName}
	{network}
	contractAddress={erc20ContractAddress}
	{maxAmount}
	bind:open={showDepositDialog}
	on:closed={(e) => {
		if (e.detail.refresh) {
			refreshWallets(service, $userInfo, $activeWallet);
		}
	}}
/>
<div class="flex flex-col flex-grow items-center">
	<div class="flex flex-col w-full lg:max-w-[1284px] xl:min-w-[1284px] lg:mx-8 mt-16">
		{#if $activeWallet}
			<div class="panel">
				<div class="flex flex-col items-start gap-6">
					<FontSpan varient="body-B-06" class="text-gray-70">Game account</FontSpan>
					<div class="flex items-start gap-6">
						{#if !!balance}
							<BalanceCard unit="plain" balance={balance.Cring} symbol="Cring" icon="cring20" />
						{/if}
					</div>
				</div>
				<div class="flex flex-col items-start gap-6">
					<FontSpan varient="body-B-06" class="text-gray-70">External wallet</FontSpan>
					<div class="flex items-start gap-6">
						<LoadingBalaceCard loading={!walletBalances}>
							<BalanceCard balance={walletBalances?.ethBalance} symbol="ETH" icon="mainnet-eth22" />
						</LoadingBalaceCard>

						<LoadingBalaceCard loading={!walletBalances}>
							<BalanceCard
								balance={walletBalances?.wETHBalance}
								symbol="wETH"
								icon="mainnet-weth22"
								addButton
								on:addButton={() => depositeHandler('mainnet')}
							/>
						</LoadingBalaceCard>

						<LoadingBalaceCard loading={!walletBalances}>
							<BalanceCard
								balance={walletBalances?.maticBalance}
								symbol="MATIC"
								icon="polygon-matic22"
							/>
						</LoadingBalaceCard>
						<LoadingBalaceCard loading={!walletBalances}>
							<BalanceCard
								balance={walletBalances?.wMaticBalance}
								symbol="wMATIC"
								icon="polygon-wmatic22"
								addButton
								on:addButton={() => depositeHandler('polygon')}
							/>
						</LoadingBalaceCard>
					</div>
					<div class="flex items-start gap-6">
						<LoadingBalaceCard loading={!walletBalances}>
							<NftBalanceCard balance={walletBalances?.tankBalance} />
						</LoadingBalaceCard>
					</div>
				</div>
			</div>
		{:else}
			<EmptyState desciption="Register your wallet to see balance">
				<img class="object-fill w-64 my-8" src="/images/zero-state.jpg" alt="zero state" />
			</EmptyState>
		{/if}
	</div>
</div>

<style>
	.panel {
		display: flex;
		width: 1284px;
		padding: 0px 16px;
		flex-direction: column;
		align-items: flex-start;
		gap: 48px;
	}
</style>
